import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import 'rxjs/add/operator/toPromise';
import 'rxjs/add/observable/of';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs/Observable';
import { catchError, tap } from 'rxjs/operators';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { Provider } from './provider.model';
import { CognitoService } from '../../services/cognito/cognito.service';
import { AwsSignerService } from '../../services/aws-signer/aws-signer.service';
import { Encoder } from '../../utils/encoder';
import { escape } from 'lodash';
import { REGIONS } from '../../services/constants/region-list';
import { PARTIES } from '../../services/constants/party-list';
import { COUNTRIES } from '../../services/constants/country-list';
import { BUS } from '../../services/constants/business-unit-list';
import { ProviderListResponse } from './provider-list-reponse.model';

@Injectable()
export class ProviderService {
  hasJustRegistered: boolean;
  hasJustUpdated: boolean;
  hasJustDeleted: boolean;

  constructor(private http: HttpClient,
    private cognitoService: CognitoService,
    private awsSignerService: AwsSignerService) {

    this.hasJustRegistered = false;
    this.hasJustUpdated = false;
  }

  getProviderList(nextPageKey): Observable<any> {
    let url = this.getUrl();
    if (nextPageKey) {
      const encodedNextPageKey = Encoder.encode(nextPageKey);
      url = url.concat('?nextPageKey=' + encodedNextPageKey);
    }

    const awsHeaders = this.awsSignerService.getAwsHeader(url, 'GET');

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    if (this.hasJustRegistered) {
      // headerParams['Cache-Control'] = 'max-age=0';
    }

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };

    return this.http.get<ProviderListResponse>(url, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  saveProvider(provider: Provider): Observable<any> {

    delete provider.providerId;

    // const jwtToken = this.cognitoService.getJwtToken();
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Content-Type': 'application/json',
    //     'Authorization': jwtToken,
    //     'x-api-key': environment.cesAPI.apiKey
    //   })
    // };


    const awsHeaders = this.awsSignerService.getAwsHeader(this.getUrl(), 'POST', provider);

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };

    return this.http.post<Provider>(this.getUrl(), provider, httpOptions)
      .pipe(
        tap(() => this.hasJustRegistered = true),
        catchError(err => this.handleError(err))
      );
  }

  getProviderById(providerId): Observable<any> {
    const encodedId = Encoder.encode(providerId);
    const awsHeaders = this.awsSignerService.getAwsHeader(`${this.getUrl()}/${encodedId}`, 'GET');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.get<Provider>(`${this.getUrl()}/${encodedId}`, httpOptions)
      .pipe(catchError(err => this.handleError(err)));
  }

  deleteProviderById(providerId): Observable<any> {
    const encodedId = Encoder.encode(providerId);
    const awsHeaders = this.awsSignerService.getAwsHeader(`${this.getUrl()}/${encodedId}`, 'DELETE');

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': awsHeaders['Authorization'],
        'X-Amz-Date': awsHeaders['X-Amz-Date'],
        'x-amz-security-token': awsHeaders['x-amz-security-token'],
        'x-api-key': environment.cesAPI.apiKey
      })
    };

    return this.http.delete<Provider>(`${this.getUrl()}/${encodedId}`, httpOptions)
      .pipe(
        tap(() => this.hasJustDeleted = true),
        catchError(err => this.handleError(err))
      );
  }

  updateProvider(provider: Provider): Observable<any> {

    const urlUpdate = `${this.getUrl()}/${Encoder.encode(provider.providerId)}`;
    // const jwtToken = this.cognitoService.getJwtToken();
    // const httpOptions = {
    //   headers: new HttpHeaders({
    //     'Authorization': jwtToken,
    //     'x-api-key': environment.cesAPI.apiKey
    //   })
    // };


    const awsHeaders = this.awsSignerService.getAwsHeader(urlUpdate, 'PUT', provider);

    const headerParams = {
      'Content-Type': 'application/json',
      'Authorization': awsHeaders['Authorization'],
      'X-Amz-Date': awsHeaders['X-Amz-Date'],
      'x-amz-security-token': awsHeaders['x-amz-security-token'],
      'x-api-key': environment.cesAPI.apiKey,
    };

    if (this.hasJustRegistered) {
      // headerParams['Cache-Control'] = 'max-age=0';
    }

    const httpOptions = {
      headers: new HttpHeaders(headerParams)
    };
    
    return this.http.put<Provider>(urlUpdate, provider, httpOptions)
      .pipe(
        tap(() => this.hasJustUpdated = true),
        catchError(err => this.handleError(err))
      );
  }

  getPartyList(): Observable<any> {
    return Observable.of(PARTIES);
  }

  getRegionList(): Observable<any> {
    return this.http.get<Array<any>>(this.getConfigUrl() + "/management-region.json")
      .pipe(catchError(err => this.handleError(err)));
  }

  private getConfigUrl() {
    return environment.configBasePath;
  }

  private getUrl() {
    return `${localStorage.getItem('region-api')}/v1/datamart/manage/providers`;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error && error.error.message === 'The security token included in the request is expired') {
      this.cognitoService.logout();
      return;
    }
    if (error.error instanceof ErrorEvent) {
      // A provider-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an ErrorObservable with a user-facing error message
    return new ErrorObservable(
      'Something bad happened. Please try again later.');
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/provider/provider.service.ts